import type { Container } from "pixi.js";
import { clamp, damp, type Vec2 } from "@shared/math";

export interface CameraFollowOpts {
  lerp?: number;
  deadzone?: { x: number; y: number };
}

const DEFAULT_FOLLOW: Required<CameraFollowOpts> = {
  lerp: 8,
  deadzone: { x: 0, y: 0 },
};

export class Camera2D {
  x = 0;
  y = 0;
  zoom = 1;

  private targetX = 0;
  private targetY = 0;
  private targetZoom = 1;
  private followTarget: Vec2 | undefined;
  private followOpts: Required<CameraFollowOpts> = DEFAULT_FOLLOW;
  private shakeAmplitude = 0;
  private shakeUntil = 0;

  minZoom = 0.25;
  maxZoom = 4;

  constructor(public viewportWidth: number, public viewportHeight: number) {}

  setViewport(w: number, h: number): void {
    this.viewportWidth = w;
    this.viewportHeight = h;
  }

  follow(target: Vec2, opts: CameraFollowOpts = {}): void {
    this.followTarget = target;
    this.followOpts = {
      lerp: opts.lerp ?? DEFAULT_FOLLOW.lerp,
      deadzone: opts.deadzone ?? DEFAULT_FOLLOW.deadzone,
    };
  }

  unfollow(): void {
    this.followTarget = undefined;
  }

  shake(amplitude: number, durationSeconds: number): void {
    this.shakeAmplitude = Math.max(this.shakeAmplitude, amplitude);
    this.shakeUntil = performance.now() + durationSeconds * 1000;
  }

  setZoom(z: number, instant = false): void {
    this.targetZoom = clamp(z, this.minZoom, this.maxZoom);
    if (instant) this.zoom = this.targetZoom;
  }

  update(dt: number): void {
    if (this.followTarget) {
      const dx = this.followTarget.x - this.targetX;
      const dy = this.followTarget.y - this.targetY;
      if (Math.abs(dx) > this.followOpts.deadzone.x) this.targetX = this.followTarget.x;
      if (Math.abs(dy) > this.followOpts.deadzone.y) this.targetY = this.followTarget.y;
    }

    this.x = damp(this.x, this.targetX, this.followOpts.lerp, dt);
    this.y = damp(this.y, this.targetY, this.followOpts.lerp, dt);
    this.zoom = damp(this.zoom, this.targetZoom, 6, dt);

    if (this.shakeUntil > performance.now()) {
      const remainingMs = this.shakeUntil - performance.now();
      const fade = Math.min(1, remainingMs / 250);
      const a = this.shakeAmplitude * fade;
      this.x += (Math.random() - 0.5) * a * 2;
      this.y += (Math.random() - 0.5) * a * 2;
    } else {
      this.shakeAmplitude = 0;
    }
  }

  /** Apply current camera transform to the world root container. Call once per frame. */
  apply(worldRoot: Container): void {
    worldRoot.scale.set(this.zoom);
    worldRoot.position.set(
      this.viewportWidth / 2 - this.x * this.zoom,
      this.viewportHeight / 2 - this.y * this.zoom,
    );
  }

  worldToScreen(world: Vec2): Vec2 {
    return {
      x: this.viewportWidth / 2 + (world.x - this.x) * this.zoom,
      y: this.viewportHeight / 2 + (world.y - this.y) * this.zoom,
    };
  }

  screenToWorld(screen: Vec2): Vec2 {
    return {
      x: this.x + (screen.x - this.viewportWidth / 2) / this.zoom,
      y: this.y + (screen.y - this.viewportHeight / 2) / this.zoom,
    };
  }

  /** AABB of the visible world area in world coordinates. */
  getViewBounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    const halfW = this.viewportWidth / (2 * this.zoom);
    const halfH = this.viewportHeight / (2 * this.zoom);
    return {
      minX: this.x - halfW,
      minY: this.y - halfH,
      maxX: this.x + halfW,
      maxY: this.y + halfH,
    };
  }
}
